/**
 * Business Validation Rules
 *
 * This module contains business logic validators used with Zod schemas.
 * These validators implement complex business rules and are separated
 * from schema definitions to follow SRP.
 *
 * Each validator:
 * - Takes data and Zod context
 * - Adds validation issues when rules are violated
 * - Can be reused across different schemas
 */

import { z } from 'zod';
import { docLengthMessage, isDocIncomplete } from '@components/molecules/add-commercial-group/utils';

/**
 * Validates commercial group payload fields
 * Ensures required fields are present and valid
 */
export const validateCommercialGroup = (
  data: {
    name?: string | null;
    legalName?: string | null;
    docType?: string | null;
    docNumber?: string | null;
    origination?: string | null;
  },
  ctx: z.RefinementCtx,
): void => {
  // Validate name
  if (!data.name || (data.name && (data.name.length < 3 || data.name.trim() === ''))) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['name'],
      message: 'Nome fantasia é obrigatório',
    });
  }

  // Validate legal name
  if (!data.legalName || (data.legalName && (data.legalName.length < 2 || data.legalName.trim() === ''))) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['legalName'],
      message: 'Razão social é obrigatória',
    });
  }

  // Validate document type
  if (data.docType && data.docType !== 'CPF' && data.docType !== 'CNPJ') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['docType'],
      message: 'Tipo de documento é obrigatório',
    });
  }

  // Validate document number
  if (!data.docNumber || data.docNumber.trim() === '') {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['docNumber'],
      message: 'Número do documento é obrigatório',
    });
  } else if ((data.docType === 'CPF' || data.docType === 'CNPJ') && isDocIncomplete(data.docType, data.docNumber)) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['docNumber'],
      message: docLengthMessage(data.docType),
    });
  }

  // Validate origination
  if (!data.origination) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      path: ['origination'],
      message: 'Originação é obrigatória',
    });
  }
};
